import type { GetServerSidePropsContext } from 'next';
import api from 'base/utils/api';
import { getTimeStamp, getUTCTimeStamp } from 'base/utils/date';
import { transPageConfig } from 'mod/featureV2/utils';
import { IPageConfig, IServerProps } from 'mod/brochure/interface';
import type { IAddCartItem, ISpuInfo, IVariant } from 'base/interfaces/product';
import { getProductSpu } from '@base/product/services';
import { isPhone } from 'base/utils/client';
import { transJsonKeys } from 'ava-util/dist/data';

// 添加购物车
export async function postAddCart(cartItems: IAddCartItem[], addType: string = 'defaultAdd'): Promise<{
    addFailProductName: string[]
}> {
    return api.post('/web/cart/v2/add', {
        data: { cartItems, addType }
    });
}

export async function getPageConfig(code: string, mock: boolean, theme?: string | string[]): Promise<IPageConfig> {
    if (mock) {
        return api.get('/api/featureConfig/brochure', {
            params: {
                theme
            }
        });
    } else {
        return api.get('/web/featureConfig/brochure', {
            params: {
                name: code,
            }
        }).then(res => {
            return {
                ...res,
                features: res.features?.map(feature => {
                    switch (feature.type) {
                        case 'tiledBanner':
                            return {
                                ...feature,
                                type: 'banner',
                                config: {
                                    ...feature.config,
                                    type: 'grid',
                                    grid: {
                                        column: feature.config?.column || 1,
                                        gutter: feature.config?.spaceBetweenColumns || 0,
                                    }
                                },
                                list: feature.list?.map(banner => {
                                    if (banner.video) {
                                        const { video, ...rest } = banner;

                                        return transJsonKeys({ ...rest, ...video }, {
                                            bgColor: 'bgColor',
                                            videoUrl: 'src',
                                            videoBackUpUrl: 'backup',
                                            picUrl: 'poster',
                                            linkUrl: 'href',
                                        })

                                    } else if ('videoUrl' in banner && !!banner.videoUrl) {
                                        return {
                                            ...transJsonKeys(banner, {
                                                bgColor: 'bgColor',
                                                videoUrl: 'src',
                                                videoBackUpUrl: 'backup',
                                                picUrl: 'poster',

                                            }),
                                            control: {
                                                size: isPhone ? 40 : 80,
                                            },
                                            controls: true
                                        };
                                    } else {
                                        return transJsonKeys(banner, {
                                            bgColor: 'bgColor',
                                            picUrl: 'src',
                                            largePicUrl: 'large',
                                            linkUrl: 'href',
                                            altText: 'alt',
                                        }, [])
                                    }
                                })
                            };


                        case 'faqs':
                            return {
                                ...feature,
                                type: 'collapse',
                                list: feature.list?.map(item => ({
                                    title: item.question,
                                    content: [item.answer]
                                }))
                            };
                        default:
                            return feature;
                    }
                })
            }
        });
    }
}

function getVariant(spu: ISpuInfo, variantId: string, pageConfig: IPageConfig): IVariant | undefined {
    const variant = spu?.productVariants.find(v => v.productVariantId === variantId);
    if (variant) {
        let isOnSale = true;
        if (pageConfig.onSaleTime) {
            const startTime = getUTCTimeStamp(pageConfig.onSaleTime);
            isOnSale = getTimeStamp() > startTime;
        }
        variant.disableActions = isOnSale ? (pageConfig.disableActions || []) : ['addToCart', 'buyNow'];
    }
    return variant
}

// 服务端获取数据
export async function getServerSideProps({ query, res }: GetServerSidePropsContext) {
    const code = query.code as string || '';
    const errors: Error[] = [];
    const serverProps: IServerProps = { features: [] };

    const pageConfig = await getPageConfig(code, query.mock === '1', query.theme)
        .then(res => transPageConfig(res))
        .catch(err => {
            errors.push(err);
            return null;
        });

    if (pageConfig) {
        serverProps.features = pageConfig.features || [];
        if (pageConfig.containerTheme) {
            serverProps.containerTheme = pageConfig.containerTheme;
        }
        if (pageConfig.featureTheme) {
            serverProps.featureTheme = pageConfig.featureTheme;
        }

        let variantId = pageConfig.variantId || '';
        if (query.mock === '1' && query.vid) {
            // mock 测试
            variantId = query.vid as string;
        }

        if (variantId) {
            const spu = await getProductSpu({ variantId: variantId }).catch(err => {
                res.statusCode = err.code >= 200001 && err.code <= 200006 ? 404 : 500;
                errors.push(err);
                return null;
            });
            if (spu) {
                const variant = getVariant(spu, variantId, pageConfig);
                if (variant) {
                    serverProps.variant = variant;
                }
            }
        }
    }

    return {
        props: errors.length
            ? { errors: JSON.parse(JSON.stringify(errors)) }
            : serverProps,
    }
}